import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MonoTypeOperatorFunction, Observable, defer, of, throwError, timer } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { RequestsApiService } from '../api/services/requests-api.service';
import { RequestBoardPublicConfigDto } from '../api/models';

const DISABLED_CODE = 'REQUEST_BOARD.DISABLED';

function errorCodeOf(value: unknown): unknown {
  return typeof value === 'object' && value !== null ? (value as { errorCode?: unknown }).errorCode : undefined;
}

/**
 * The board is switched off: a 503 `REQUEST_BOARD.DISABLED` answer — the raw
 * `HttpErrorResponse` or an error already mapped by `mapRequestBoardError`.
 */
export function isRequestBoardDisabledError(err: unknown): boolean {
  const code = err instanceof HttpErrorResponse ? errorCodeOf(err.error) : errorCodeOf(err);
  return code === DISABLED_CODE;
}

/** Config (and its formToken) is refreshed when older than this. */
export const REQUEST_BOARD_CONFIG_MAX_AGE_MS = 90 * 60 * 1000;
/**
 * A config saying the board is OFF is re-checked this often instead, so the
 * entries (header, footer, CTAs) come back soon after it is switched on again.
 */
export const REQUEST_BOARD_DISABLED_CONFIG_MAX_AGE_MS = 5 * 60 * 1000;
/**
 * The backend rejects a formToken younger than 3 s (bot guard) — a token is
 * only handed out for submission once it is at least this old (small margin
 * for clock skew between "response received" and "issued").
 */
export const FORM_TOKEN_MIN_AGE_MS = 3_500;
/**
 * The backend rejects a formToken older than 2 h — a cached token is reused
 * for submission until this age (leaves ≥ 5 min for the request itself),
 * so a submit right after the 90-min config refresh does not hit the 3 s
 * "too fresh" window.
 */
export const FORM_TOKEN_MAX_AGE_MS = 115 * 60 * 1000;

/**
 * Signal cache of GET /public/requests/config. The response carries the
 * HMAC `formToken` (valid 3 s – 2 h), so the cache is refreshed when older
 * than 90 minutes (5 minutes while it says the board is off), on explicit
 * `refresh()` (e.g. after FORM_TOKEN_INVALID) and after a board call answered
 * DISABLED (`markDisabled()` invalidates it).
 */
@Injectable({ providedIn: 'root' })
export class RequestBoardConfigService {
  private readonly api = inject(RequestsApiService);

  private readonly _config = signal<RequestBoardPublicConfigDto | null>(null);
  private readonly _loadedAt = signal<number | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal(false);
  /**
   * A board endpoint answered 503 `REQUEST_BOARD.DISABLED` (reported via
   * `markDisabled()`) — the board is off even if the cached config still says
   * `enabled: true`. Cleared by the next config fetched from the API.
   */
  private readonly _disabledByApi = signal(false);
  /** Bumped by every `markDisabled()` — tells configs requested before it apart. */
  private disabledEpoch = 0;
  private inFlight$: Observable<RequestBoardPublicConfigDto> | null = null;

  readonly config = this._config.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly loadedAt = this._loadedAt.asReadonly();
  readonly formToken = computed(() => this._config()?.formToken ?? null);
  /**
   * Optimistic: true until the config says `enabled: false` or any board call
   * answered 503 `REQUEST_BOARD.DISABLED` (so entries do not flicker while the
   * config is loading). `!enabled()` = „the board is known to be off”.
   */
  readonly enabled = computed(() => !this._disabledByApi() && (this._config()?.enabled ?? true));
  /**
   * Pessimistic: true only once a config with `enabled: true` is loaded (and no
   * board call answered DISABLED since) — for entry points shown on pages that
   * may never load the config otherwise (e.g. the footer).
   */
  readonly enabledConfirmed = computed(() => !this._disabledByApi() && this._config()?.enabled === true);
  readonly offersConsentMode = computed(() => this._config()?.offersConsentMode ?? 'separate_checkbox');
  readonly nameRequired = computed(() => this._config()?.nameRequired ?? true);
  readonly headerMinActive = computed(() => this._config()?.headerMinActive ?? 0);
  /** „Wklej post z FB” box — only when the backend advertises the extract endpoint. */
  readonly extractEnabled = computed(() => this._config()?.extractEnabled === true);

  /**
   * True when a config is cached and younger than the max age (90 min, or
   * 5 min when it says the board is off) — false after `markDisabled()` until
   * the next config arrives.
   */
  isFresh(now: number = Date.now()): boolean {
    const config = this._config();
    const loadedAt = this._loadedAt();
    if (config === null || loadedAt === null) return false;
    const maxAge = config.enabled ? REQUEST_BOARD_CONFIG_MAX_AGE_MS : REQUEST_BOARD_DISABLED_CONFIG_MAX_AGE_MS;
    return now - loadedAt < maxAge;
  }

  /**
   * Returns the cached config when fresh, otherwise fetches it (in-flight
   * request is shared between callers).
   */
  load(force = false): Observable<RequestBoardPublicConfigDto> {
    const cached = this._config();
    if (!force && cached && this.isFresh()) {
      return of(cached);
    }
    if (this.inFlight$) {
      return this.inFlight$;
    }
    // Flags and the in-flight slot are only claimed on actual subscription.
    const request$ = defer(() => {
      this._loading.set(true);
      this._error.set(false);
      const disabledEpoch = this.disabledEpoch;
      return this.api.getConfig().pipe(
        tap((config) => {
          this._config.set(config);
          // A config requested AFTER the last DISABLED answer is authoritative
          // (it reports the effective `enabled`). An older one is not: the
          // board stays off and the cache stays invalid (re-checked next time).
          if (disabledEpoch !== this.disabledEpoch) return;
          this._loadedAt.set(Date.now());
          this._disabledByApi.set(false);
        }),
      );
    }).pipe(
      catchError((err: unknown) => {
        this._error.set(true);
        return throwError(() => err);
      }),
      finalize(() => {
        this._loading.set(false);
        this.inFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.inFlight$ = request$;
    return request$;
  }

  /** Fire-and-forget variant for eager warm-up (errors are swallowed). */
  ensure(): void {
    if (this.isFresh() || this.inFlight$) return;
    this.load().pipe(catchError(() => of(null))).subscribe();
  }

  /** Forces a new GET (fresh formToken). */
  refresh(): Observable<RequestBoardPublicConfigDto> {
    return this.load(true);
  }

  /**
   * A board endpoint answered 503 `REQUEST_BOARD.DISABLED`: switch `enabled()`
   * off right away (entries hide) instead of waiting for the next config fetch,
   * and invalidate the cached config (it said `enabled: true`) — the next
   * `ensure()` / `load()` asks the API again, so the entries come back as soon
   * as the board is switched on again (not 90 minutes later). Called by
   * `RequestBoardStore` for every board call; components calling
   * `RequestsApiService` directly use `markDisabledOnError()`.
   */
  markDisabled(): void {
    this.disabledEpoch++;
    this._disabledByApi.set(true);
    this._loadedAt.set(null);
  }

  /**
   * For board calls made outside `RequestBoardStore` (live counter, „Wklej
   * post”, „Moje zapytania”): a 503 `REQUEST_BOARD.DISABLED` answer switches
   * the board off (`markDisabled`); values and errors pass through unchanged.
   * `api.count(c).pipe(config.markDisabledOnError(), …)`
   */
  markDisabledOnError<T>(): MonoTypeOperatorFunction<T> {
    return tap<T>({
      error: (err: unknown) => {
        if (isRequestBoardDisabledError(err)) this.markDisabled();
      },
    });
  }

  /**
   * A formToken that the backend will accept right now (age between 3 s and
   * 2 h): the cached one when it is younger than `FORM_TOKEN_MAX_AGE_MS`
   * (waiting out the 3 s minimum age if needed), otherwise a freshly fetched
   * one after the minimum age has elapsed (also right after `markDisabled()`,
   * which voids the cache). Use this for submissions instead of `load()`,
   * which refreshes at 90 min and would hand out a too-fresh token.
   */
  freshFormToken(now: number = Date.now()): Observable<string> {
    const cached = this._config();
    const loadedAt = this._loadedAt();
    if (cached && loadedAt !== null && now - loadedAt < FORM_TOKEN_MAX_AGE_MS) {
      const wait = Math.max(0, FORM_TOKEN_MIN_AGE_MS - (now - loadedAt));
      return wait === 0 ? of(cached.formToken) : timer(wait).pipe(map(() => cached.formToken));
    }
    return this.load(true).pipe(
      switchMap((config) => timer(FORM_TOKEN_MIN_AGE_MS).pipe(map(() => config.formToken))),
    );
  }
}
